import { useState } from "react";
import { Info, ChevronDown, ChevronUp, BadgeCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const PAY_LATER_REASONS = [
  "You're unsure about your registration fee.",
  "You need clarification before making payment.",
  "You prefer to pay at the conference venue.",
  "You would like assistance from the registration team.",
];

export function RegistrationInfoRibbon() {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card
      className={cn(
        "w-full border-sky-200 bg-gradient-to-br from-sky-50 to-amber-50/60 shadow-md overflow-hidden transition-all"
      )}
    >
      <CardContent className="p-0">
        {/* Header — always visible */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
              <Info className="w-4 h-4" />
            </div>
            <h3 className="font-semibold text-slate-800 text-sm sm:text-base truncate">
              Need Help with Registration Fees?
            </h3>
          </div>
          <span className="shrink-0 text-sky-700">
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </span>
        </button>

        {/* Expandable body */}
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-in-out",
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="overflow-hidden">
            <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0 space-y-3.5">
              {/* Good news block */}
              <div className="rounded-lg bg-white/70 border border-sky-100 p-3.5 sm:p-4 space-y-3">
                <p className="text-sm sm:text-base font-semibold text-sky-800 mb-1.5">
                  <span className="mr-1" aria-hidden>ℹ️</span>
                  Good News!
                </p>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  Your registration fee is calculated automatically based on the registration category and attendee information you provide.
                </p>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  If you are unsure about the total amount, have questions about the registration fees or pricing, or would like assistance in selecting the correct registration option, please feel free to contact your Regional Coordinator using the contact details provided below. We will be happy to assist you.
                </p>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  If you prefer not to make the payment online at this time, simply select <strong>"Pay Later"</strong> during checkout. You can complete your payment when you arrive at the conference during on-site registration.
                </p>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  <span className="mr-1" aria-hidden>💙</span>
                  Our goal is to make your registration experience simple, transparent, and hassle-free.
                </p>
              </div>

              {/* Friendly reminder */}
              <div>
                <p className="text-xs sm:text-sm font-semibold text-slate-800 mb-2">
                  Choose "Pay Later" if:
                </p>
                <ul className="grid gap-1.5 sm:gap-2">
                  {PAY_LATER_REASONS.map((reason, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-xs sm:text-sm text-slate-700"
                    >
                      <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span className="leading-snug">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Success badge */}
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-200 px-3 py-1.5 text-emerald-800 text-xs sm:text-sm font-semibold w-full sm:w-auto justify-center sm:justify-start">
                <BadgeCheck className="w-4 h-4 shrink-0" />
                Registration First — Payment Later is Absolutely Fine!
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
